import React, { useState, useEffect } from 'react';
import {
  Building2,
  Users,
  Layers,
  Info,
  Plus,
  Edit3,
  ChevronRight,
  Save,
  AlertTriangle,
  Trash2,
  X,
  Check,
  UserPlus,
  ChevronDown,
  Network,
  Briefcase,
  Table,
  GitBranch
} from 'lucide-react';
import { api } from '../lib/api';

interface Project {
  id: string;
  name: string;
  code?: string;
}

interface Building {
  id: string;
  name: string;
  project_id: string;
  floor_count?: number;
  type?: string;
  status?: 'Active' | 'Planning' | 'Completed';
}

interface ConstructionStage {
  id: string;
  name: string;
  display_order: number;
}

interface WorkCategory {
  id: string;
  stage_id: string;
  name: string;
  uom?: string;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  projectId: number;
  reportsTo?: number;
  managerName?: string;
  buildingId?: string;
  buildingName?: string;
}

interface ProjectStats {
  totalTasks: number;
  completed: number;
  inProgress: number;
  delayed: number;
}

type TabType = 'general' | 'buildings' | 'categories' | 'team';

interface ProjectDetailsPageProps {
  projectId: string;
  projects: Project[];
  onProjectChange: (projectId: string) => void;
  // Theme is managed globally via <AppSidebar>; passed through only so the view
  // can branch on it if needed (currently unused — all dark styling uses the
  // Tailwind `dark:` variant driven by the <html class="dark">).
  theme?: 'light' | 'dark';
}

const ProjectDetailsPage: React.FC<ProjectDetailsPageProps> = ({
  projectId,
  projects,
  onProjectChange,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [stages, setStages] = useState<ConstructionStage[]>([]);
  const [categories, setCategories] = useState<WorkCategory[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<ProjectStats>({ totalTasks: 0, completed: 0, inProgress: 0, delayed: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [projectForm, setProjectForm] = useState({
    name: '',
    clientName: '',
    startDate: '',
    endDate: '',
    description: ''
  });

  // Modal states
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [newBuilding, setNewBuilding] = useState({ name: '', floors: '', type: 'Residential' });
  const [newTeamMember, setNewTeamMember] = useState({ name: '', role: '', reportsTo: '', buildingId: '' });
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  
  // Team management enhanced states
  const [teamViewMode, setTeamViewMode] = useState<'table' | 'hierarchy'>('hierarchy');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showMemberPanel, setShowMemberPanel] = useState(false);
  const [addingSubordinateTo, setAddingSubordinateTo] = useState<number | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const selectedProject = projects.find(p => p.id === projectId);

  // Load data when project changes
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [masterData, team] = await Promise.all([
          api.getMasterData(projectId),
          api.fetchTeam(projectId)
        ]);

        setBuildings(masterData.buildings || []);
        setStages(masterData.categories || []);
        setCategories(masterData.subcategories || []);
        setTeamMembers(team || []);

        // Set project form
        if (selectedProject) {
          setProjectForm({
            name: selectedProject.name || '',
            clientName: 'Harita Groups Ltd.',
            startDate: '2023-01-15',
            endDate: '2025-12-30',
            description: 'Residential complex construction including multiple towers and amenities.'
          });
        }

        // Calculate stats from tasks
        try {
          const tasks = await api.getTasks(projectId);
          const completed = tasks.filter((t: any) => t.status === 'Completed').length;
          const inProgress = tasks.filter((t: any) => t.status === 'In Progress').length;
          setStats({
            totalTasks: tasks.length,
            completed,
            inProgress,
            delayed: Math.floor(tasks.length * 0.02) // Mock delayed calculation
          });
        } catch {
          setStats({ totalTasks: 0, completed: 0, inProgress: 0, delayed: 0 });
        }
      } catch (error) {
        console.error('Failed to load project data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId, selectedProject]);

  const handleAddTeamMember = async () => {
    if (!newTeamMember.name || !newTeamMember.role) return;

    try {
      await api.addTeamMember({
        name: newTeamMember.name,
        role: newTeamMember.role,
        projectId: parseInt(projectId),
        reportsTo: newTeamMember.reportsTo ? parseInt(newTeamMember.reportsTo) : undefined,
        buildingId: newTeamMember.buildingId || undefined
      });

      // Reload team
      const team = await api.fetchTeam(projectId);
      setTeamMembers(team);
      setNewTeamMember({ name: '', role: '', reportsTo: '', buildingId: '' });
      setShowAddTeamMember(false);
      setAddingSubordinateTo(null);
    } catch (error) {
      console.error('Failed to add team member:', error);
    }
  };

  const handleAddSubordinate = (managerId: number) => {
    setAddingSubordinateTo(managerId);
    setNewTeamMember({ name: '', role: '', reportsTo: String(managerId), buildingId: '' });
    setShowAddTeamMember(true);
  };

  const handleSelectMember = (member: TeamMember) => {
    setSelectedMember(member);
    setShowMemberPanel(true);
  };

  const toggleNodeExpanded = (memberId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(memberId)) {
      newExpanded.delete(memberId);
    } else {
      newExpanded.add(memberId);
    }
    setExpandedNodes(newExpanded);
  };

  const closeMemberPanel = () => {
    setSelectedMember(null);
    setShowMemberPanel(false);
    setEditingMember(null);
  };

  const handleUpdateTeamMember = async () => {
    if (!editingMember) return;

    try {
      await api.updateTeamMember(editingMember.id, {
        name: editingMember.name,
        role: editingMember.role,
        reportsTo: editingMember.reportsTo
      });

      const team = await api.fetchTeam(projectId);
      setTeamMembers(team);
      setEditingMember(null);
    } catch (error) {
      console.error('Failed to update team member:', error);
    }
  };

  const handleDeleteTeamMember = async (id: number) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      await api.deleteTeamMember(id);
      const team = await api.fetchTeam(projectId);
      setTeamMembers(team);
    } catch (error: any) {
      alert(error.message || 'Failed to delete team member');
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const completionPercent = stats.totalTasks > 0
    ? Math.round((stats.completed / stats.totalTasks) * 100)
    : 0;

  const tabs = [
    { id: 'general' as TabType, label: 'General Info', icon: Info },
    { id: 'buildings' as TabType, label: 'Buildings & Units', icon: Building2 },
    { id: 'categories' as TabType, label: 'Work Categories', icon: Layers },
    { id: 'team' as TabType, label: 'Team Hierarchy', icon: Users }
  ];

  // Build team hierarchy
  const buildTeamTree = () => {
    const managers = teamMembers.filter(m => !m.reportsTo);
    const getSubordinates = (managerId: number): TeamMember[] => {
      return teamMembers.filter(m => m.reportsTo === managerId);
    };
    return { managers, getSubordinates };
  };

  const { managers, getSubordinates } = buildTeamTree();

  const renderTeamMember = (member: TeamMember, level: number = 0) => {
    const subordinates = getSubordinates(member.id);
    const isEditing = editingMember?.id === member.id;

    return (
      <div key={member.id} className={`${level > 0 ? 'ml-8 pl-4 border-l-2 border-gray-200' : ''}`}>
        <div className="flex items-center p-3 rounded-lg hover:bg-gray-50 group transition-colors">
          {level > 0 && <div className="w-4 h-0.5 bg-gray-200 -ml-4 mr-2" />}
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${
            level === 0 ? 'bg-blue-100 text-blue-600' :
            level === 1 ? 'bg-purple-100 text-purple-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            {getInitials(member.name)}
          </div>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={e => setEditingMember({ ...editingMember, name: e.target.value })}
                  className="text-sm border rounded px-2 py-1 w-40"
                />
                <input
                  type="text"
                  value={editingMember.role}
                  onChange={e => setEditingMember({ ...editingMember, role: e.target.value })}
                  className="text-sm border rounded px-2 py-1 w-40"
                />
                <button onClick={handleUpdateTeamMember} className="text-green-600 hover:text-green-700">
                  <Check size={16} />
                </button>
                <button onClick={() => setEditingMember(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                <div className="flex items-center gap-2">
                  <p className={`text-xs ${level === 0 ? 'text-blue-600' : level === 1 ? 'text-purple-600' : 'text-gray-500'}`}>
                    {member.role}
                  </p>
                  {member.buildingName && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800">
                      {member.buildingName}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          {!isEditing && (
            <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-2">
              <button
                onClick={() => setEditingMember(member)}
                className="text-gray-400 hover:text-indigo-600"
              >
                <Edit3 size={16} />
              </button>
              <button
                onClick={() => handleDeleteTeamMember(member.id)}
                className="text-gray-400 hover:text-red-600"
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}
        </div>
        {subordinates.length > 0 && (
          <div className="mt-2 space-y-2">
            {subordinates.map(sub => renderTeamMember(sub, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Enhanced hierarchy node renderer with expand/collapse and add subordinate
  const renderHierarchyNode = (member: TeamMember, level: number = 0) => {
    const subordinates = getSubordinates(member.id);
    const hasSubordinates = subordinates.length > 0;
    const isExpanded = expandedNodes.has(member.id) || level < 2; // Auto-expand first 2 levels
    const isSelected = selectedMember?.id === member.id;

    return (
      <div key={member.id} className={`${level > 0 ? 'ml-6' : ''}`}>
        {/* Connector line */}
        {level > 0 && (
          <div className="flex items-center -ml-6 mb-1">
            <div className="w-6 border-b-2 border-l-2 border-gray-200 h-4 rounded-bl" />
          </div>
        )}
        
        {/* Node */}
        <div 
          className={`flex items-center p-3 rounded-lg border-2 transition-all cursor-pointer group ${
            isSelected 
              ? 'border-indigo-500 bg-indigo-50 shadow-md' 
              : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
          }`}
          onClick={() => handleSelectMember(member)}
        >
          {/* Expand/Collapse Button */}
          {hasSubordinates && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNodeExpanded(member.id);
              }}
              className="mr-2 p-1 rounded hover:bg-gray-200 text-gray-400"
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          {!hasSubordinates && <div className="w-8" />}
          
          {/* Avatar */}
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
            level === 0 ? 'bg-indigo-100 text-indigo-600' :
            level === 1 ? 'bg-purple-100 text-purple-600' :
            level === 2 ? 'bg-teal-100 text-teal-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            {getInitials(member.name)}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`text-xs font-medium ${
                level === 0 ? 'text-indigo-600' : 
                level === 1 ? 'text-purple-600' : 
                level === 2 ? 'text-teal-600' :
                'text-gray-500'
              }`}>
                {member.role}
              </p>
              {member.buildingName && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                  <Building2 size={10} />
                  {member.buildingName}
                </span>
              )}
              {hasSubordinates && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                  {subordinates.length} report{subordinates.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddSubordinate(member.id);
              }}
              className="p-1.5 rounded bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
              title="Add subordinate"
            >
              <UserPlus size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTeamMember(member.id);
              }}
              className="p-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        
        {/* Subordinates */}
        {hasSubordinates && isExpanded && (
          <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
            {subordinates.map(sub => renderHierarchyNode(sub, level + 1))}
            {/* Add sibling button at this level */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddSubordinate(member.id);
              }}
              className="ml-6 mt-1 py-2 px-3 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center gap-1"
            >
              <Plus size={12} />
              Add under {member.name.split(' ')[0]}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
        {/* Project Header */}
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">{selectedProject?.name || 'Project'}</span>
            </div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">Construction Planning</span>
          </div>
        </div>

        {/* Project Selector */}
        <div className="p-4 border-b border-gray-200">
          <label className="block text-xs font-medium text-gray-500 mb-2">Switch Project:</label>
          <select
            value={projectId}
            onChange={e => onProjectChange(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 bg-white text-gray-700 focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm"
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          <div className="group flex items-center px-3 py-2 text-sm font-medium rounded-md bg-indigo-50 text-indigo-600">
            <Building2 className="mr-3 text-indigo-600" size={20} />
            Project Details
          </div>

          <div className="pt-4 pb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Work Categories
          </div>
          {stages.slice(0, 5).map(stage => {
            const count = categories.filter(c => c.stage_id === stage.id).length;
            return (
              <div
                key={stage.id}
                className="group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100"
              >
                <Layers className="mr-3 text-gray-400" size={18} />
                {stage.name}
                <span className="ml-auto bg-gray-100 py-0.5 px-2 rounded-full text-xs text-gray-600">
                  {count}
                </span>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>{selectedProject?.name}</span>
            <ChevronRight size={14} />
            <span className="font-medium text-gray-900">Project Settings</span>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-colors">
              <Save size={16} />
              <span>Save Changes</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Page Title */}
            <div className="flex justify-between items-end mb-2">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Project Details</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Manage general information, buildings, work categories, and team structure.
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                        activeTab === tab.id
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={18} className="mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <>
                {/* General Info Tab */}
                {activeTab === 'general' && (
                  <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Project Name</label>
                        <div className="mt-1 flex rounded-md shadow-sm">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                            <Building2 size={16} />
                          </span>
                          <input
                            type="text"
                            value={projectForm.name}
                            onChange={e => setProjectForm({ ...projectForm, name: e.target.value })}
                            className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Client Name</label>
                        <input
                          type="text"
                          value={projectForm.clientName}
                          onChange={e => setProjectForm({ ...projectForm, clientName: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Project Start Date</label>
                        <input
                          type="date"
                          value={projectForm.startDate}
                          onChange={e => setProjectForm({ ...projectForm, startDate: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Project End Date (Est.)</label>
                        <input
                          type="date"
                          value={projectForm.endDate}
                          onChange={e => setProjectForm({ ...projectForm, endDate: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          rows={3}
                          value={projectForm.description}
                          onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Buildings Tab */}
                {activeTab === 'buildings' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white shadow rounded-lg border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-900">Buildings Configuration</h3>
                        <button
                          onClick={() => setShowAddBuilding(true)}
                          className="text-indigo-600 hover:text-indigo-500 text-sm font-medium flex items-center"
                        >
                          <Plus size={16} className="mr-1" />
                          Add Building
                        </button>
                      </div>
                      <ul className="divide-y divide-gray-200">
                        {buildings.map((building) => (
                          <li
                            key={building.id}
                            className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                <span className="text-indigo-600 font-bold">
                                  {building.name.charAt(0)}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{building.name}</div>
                                <div className="text-xs text-gray-500">
                                  {building.floor_count || '—'} Floors • {building.type || 'Residential'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                building.status === 'Active' || !building.status
                                  ? 'bg-green-100 text-green-800'
                                  : building.status === 'Planning'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {building.status || 'Active'}
                              </span>
                              <Edit3 size={16} className="text-gray-400 group-hover:text-gray-600" />
                            </div>
                          </li>
                        ))}
                        {buildings.length === 0 && (
                          <li className="px-6 py-8 text-center text-gray-500">
                            No buildings configured yet.
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Project Stats */}
                    <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Project Stats</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500">Total Tasks</span>
                            <span className="font-medium text-gray-900">{stats.totalTasks.toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '100%' }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500">Completed</span>
                            <span className="font-medium text-gray-900">
                              {stats.completed.toLocaleString()} ({completionPercent}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${completionPercent}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500">Delayed</span>
                            <span className="font-medium text-red-600">{stats.delayed} tasks</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-red-500 h-2 rounded-full"
                              style={{ width: `${stats.totalTasks > 0 ? (stats.delayed / stats.totalTasks) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {stats.delayed > 0 && (
                        <div className="mt-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex items-start">
                            <AlertTriangle className="text-yellow-500 mr-2 flex-shrink-0" size={20} />
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">Attention Required</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {stats.delayed} tasks are currently delayed and may affect project timeline.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Work Categories Tab */}
                {activeTab === 'categories' && (
                  <div className="bg-white shadow rounded-lg border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-lg font-medium text-gray-900">Construction Stages & Work Categories</h3>
                      <button className="text-indigo-600 hover:text-indigo-500 text-sm font-medium flex items-center">
                        <Plus size={16} className="mr-1" />
                        Add Category
                      </button>
                    </div>
                    <div className="p-6">
                      <div className="space-y-6">
                        {stages.map(stage => {
                          const stageCategories = categories.filter(c => c.stage_id === stage.id);
                          return (
                            <div key={stage.id} className="border border-gray-200 rounded-lg">
                              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                                <div className="flex items-center">
                                  <Layers size={18} className="text-indigo-600 mr-2" />
                                  <span className="font-medium text-gray-900">{stage.name}</span>
                                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-600">
                                    {stageCategories.length} categories
                                  </span>
                                </div>
                                <button className="text-gray-400 hover:text-indigo-600">
                                  <Plus size={16} />
                                </button>
                              </div>
                              {stageCategories.length > 0 && (
                                <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {stageCategories.map(cat => (
                                    <div
                                      key={cat.id}
                                      className="px-3 py-2 bg-gray-50 rounded border border-gray-200 text-sm text-gray-700 flex justify-between items-center group hover:border-indigo-300"
                                    >
                                      <span>{cat.name}</span>
                                      {cat.uom && (
                                        <span className="text-xs text-gray-400">{cat.uom}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Team Hierarchy Tab */}
                {activeTab === 'team' && (
                  <div className="flex gap-6">
                    {/* Main Team Panel */}
                    <div className={`bg-white shadow rounded-lg border border-gray-200 transition-all ${showMemberPanel ? 'flex-1' : 'w-full'}`}>
                    <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                          Organization Structure
                          <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                            {teamMembers.length} members
                          </span>
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Manage roles and reporting hierarchy for this project.
                        </p>
                      </div>
                        <div className="flex items-center gap-3">
                          {/* View Toggle */}
                          <div className="flex rounded-lg bg-gray-100 p-1">
                      <button
                              onClick={() => setTeamViewMode('table')}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                                teamViewMode === 'table' 
                                  ? 'bg-white text-indigo-600 shadow-sm' 
                                  : 'text-gray-500 hover:text-gray-900'
                              }`}
                            >
                              <Table size={14} />
                              Table
                            </button>
                            <button
                              onClick={() => setTeamViewMode('hierarchy')}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                                teamViewMode === 'hierarchy' 
                                  ? 'bg-white text-indigo-600 shadow-sm' 
                                  : 'text-gray-500 hover:text-gray-900'
                              }`}
                            >
                              <GitBranch size={14} />
                              Hierarchy
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              setAddingSubordinateTo(null);
                              setNewTeamMember({ name: '', role: '', reportsTo: '', buildingId: '' });
                              setShowAddTeamMember(true);
                            }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center shadow-sm transition-colors"
                      >
                        <Plus size={16} className="mr-2" />
                        Add Position
                      </button>
                    </div>
                      </div>
                      
                    <div className="p-6">
                        {teamViewMode === 'table' ? (
                          /* Table View */
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reports To</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Building</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {teamMembers.map(member => {
                                  const manager = member.reportsTo ? teamMembers.find(m => m.id === member.reportsTo) : null;
                                  return (
                                    <tr key={member.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 mr-3">
                                            {getInitials(member.name)}
                                          </div>
                                          <span className="text-sm font-medium text-gray-900">{member.name}</span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <span className="text-sm text-gray-600">{member.role}</span>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {manager ? (
                                          <span className="text-sm text-gray-600">{manager.name}</span>
                                        ) : (
                                          <span className="text-xs text-gray-400 italic">Top Level</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {member.buildingName ? (
                                          <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">{member.buildingName}</span>
                                        ) : (
                                          <span className="text-xs text-gray-400">All</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-right">
                                        <button
                                          onClick={() => handleSelectMember(member)}
                                          className="text-indigo-600 hover:text-indigo-800 mr-3 text-sm"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => handleDeleteTeamMember(member.id)}
                                          className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {teamMembers.length === 0 && (
                                  <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                      No team members yet. Click "Add Position" to get started.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          /* Hierarchy View */
                          managers.length > 0 ? (
                            <div className="space-y-2">
                              {managers.map(manager => renderHierarchyNode(manager, 0))}
                              
                              {/* Add Top Level Button */}
                              <button
                                onClick={() => {
                                  setAddingSubordinateTo(null);
                                  setNewTeamMember({ name: '', role: '', reportsTo: '', buildingId: '' });
                                  setShowAddTeamMember(true);
                                }}
                                className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2"
                              >
                                <Plus size={16} />
                                Add Top-Level Position
                              </button>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <Users size={48} className="mx-auto mb-4 text-gray-300" />
                          <p>No team members configured yet.</p>
                          <button
                            onClick={() => setShowAddTeamMember(true)}
                            className="mt-4 text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                          >
                            Add your first team member
                          </button>
                        </div>
                          )
                      )}
                    </div>
                      
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        Showing {teamMembers.length} team members
                      </span>
                        <span className="text-xs text-gray-400">
                          {teamViewMode === 'hierarchy' ? 'Click on a member to view details' : 'Click Edit to modify'}
                      </span>
                      </div>
                    </div>

                    {/* Side Panel for Member Details */}
                    {showMemberPanel && selectedMember && (
                      <div className="w-96 bg-white shadow-lg rounded-lg border border-gray-200 flex flex-col">
                        <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                          <h4 className="font-semibold text-gray-900">Member Details</h4>
                          <button onClick={closeMemberPanel} className="text-gray-400 hover:text-gray-600">
                            <X size={18} />
                          </button>
                        </div>
                        
                        <div className="p-5 flex-1 overflow-y-auto">
                          {/* Avatar & Name */}
                          <div className="flex items-center mb-6">
                            <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-xl font-bold text-indigo-600 mr-4">
                              {getInitials(selectedMember.name)}
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900 text-lg">{selectedMember.name}</h5>
                              <p className="text-sm text-indigo-600">{selectedMember.role}</p>
                            </div>
                          </div>

                          {/* Edit Form */}
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                              <input
                                type="text"
                                value={editingMember?.id === selectedMember.id ? editingMember.name : selectedMember.name}
                                onChange={e => setEditingMember({ ...selectedMember, ...editingMember, name: e.target.value })}
                                onFocus={() => !editingMember && setEditingMember(selectedMember)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Role / Designation</label>
                              <input
                                type="text"
                                value={editingMember?.id === selectedMember.id ? editingMember.role : selectedMember.role}
                                onChange={e => setEditingMember({ ...selectedMember, ...editingMember, role: e.target.value })}
                                onFocus={() => !editingMember && setEditingMember(selectedMember)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Reports To</label>
                              <select
                                value={editingMember?.id === selectedMember.id ? (editingMember.reportsTo || '') : (selectedMember.reportsTo || '')}
                                onChange={e => setEditingMember({ 
                                  ...selectedMember, 
                                  ...editingMember, 
                                  reportsTo: e.target.value ? parseInt(e.target.value) : undefined 
                                })}
                                onFocus={() => !editingMember && setEditingMember(selectedMember)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="">Top Level (No Manager)</option>
                                {teamMembers
                                  .filter(m => m.id !== selectedMember.id)
                                  .map(m => (
                                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                                  ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Building Assignment</label>
                              <select
                                value={editingMember?.id === selectedMember.id ? (editingMember.buildingId || '') : (selectedMember.buildingId || '')}
                                onChange={e => setEditingMember({ 
                                  ...selectedMember, 
                                  ...editingMember, 
                                  buildingId: e.target.value || undefined 
                                })}
                                onFocus={() => !editingMember && setEditingMember(selectedMember)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="">All Buildings</option>
                                {buildings.map(b => (
                                  <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Subordinates info */}
                            {getSubordinates(selectedMember.id).length > 0 && (
                              <div className="pt-4 border-t">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Direct Reports</label>
                                <div className="space-y-2">
                                  {getSubordinates(selectedMember.id).map(sub => (
                                    <div key={sub.id} className="flex items-center p-2 bg-gray-50 rounded">
                                      <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 mr-2">
                                        {getInitials(sub.name)}
                                      </div>
                                      <span className="text-sm text-gray-700">{sub.name}</span>
                                      <span className="text-xs text-gray-400 ml-auto">{sub.role}</span>
                                    </div>
                                  ))}
                    </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex gap-2">
                          {editingMember && editingMember.id === selectedMember.id && (
                            <button
                              onClick={async () => {
                                await handleUpdateTeamMember();
                                setSelectedMember(editingMember);
                              }}
                              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center justify-center gap-2"
                            >
                              <Check size={16} />
                              Save Changes
                            </button>
                          )}
                          <button
                            onClick={() => handleAddSubordinate(selectedMember.id)}
                            className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
                          >
                            <UserPlus size={16} />
                            Add Subordinate
                          </button>
                          <button
                            onClick={() => {
                              handleDeleteTeamMember(selectedMember.id);
                              closeMemberPanel();
                            }}
                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Add Team Member Modal */}
      {showAddTeamMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {addingSubordinateTo 
                    ? `Add Subordinate` 
                    : 'Add Team Member'}
                </h3>
                {addingSubordinateTo && (
                  <p className="text-sm text-gray-500 mt-1">
                    Reporting to: {teamMembers.find(m => m.id === addingSubordinateTo)?.name}
                  </p>
                )}
              </div>
              <button 
                onClick={() => {
                  setShowAddTeamMember(false);
                  setAddingSubordinateTo(null);
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users size={14} className="inline mr-1" />
                  Name
                </label>
                <input
                  type="text"
                  value={newTeamMember.name}
                  onChange={e => setNewTeamMember({ ...newTeamMember, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                  placeholder="e.g., Rahul Sharma"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Briefcase size={14} className="inline mr-1" />
                  Role / Designation
                </label>
                <input
                  type="text"
                  value={newTeamMember.role}
                  onChange={e => setNewTeamMember({ ...newTeamMember, role: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                  placeholder="e.g., Site Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Network size={14} className="inline mr-1" />
                  Reports To
                </label>
                <select
                  value={newTeamMember.reportsTo}
                  onChange={e => setNewTeamMember({ ...newTeamMember, reportsTo: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                >
                  <option value="">None (Top Level)</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Select who this person reports to in the hierarchy.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 size={14} className="inline mr-1" />
                  Building Assignment (Optional)
                </label>
                <select
                  value={newTeamMember.buildingId}
                  onChange={e => setNewTeamMember({ ...newTeamMember, buildingId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                >
                  <option value="">All Buildings / General</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Assign to a specific tower if applicable.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddTeamMember(false);
                  setAddingSubordinateTo(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTeamMember}
                disabled={!newTeamMember.name || !newTeamMember.role}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Check size={16} />
                {addingSubordinateTo ? 'Add Subordinate' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Building Modal */}
      {showAddBuilding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Building</h3>
              <button onClick={() => setShowAddBuilding(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Building Name</label>
                <input
                  type="text"
                  value={newBuilding.name}
                  onChange={e => setNewBuilding({ ...newBuilding, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g., Tower D"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Number of Floors</label>
                <input
                  type="number"
                  value={newBuilding.floors}
                  onChange={e => setNewBuilding({ ...newBuilding, floors: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g., 24"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={newBuilding.type}
                  onChange={e => setNewBuilding({ ...newBuilding, type: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Mixed">Mixed Use</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddBuilding(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // TODO: API call to add building
                  setShowAddBuilding(false);
                  setNewBuilding({ name: '', floors: '', type: 'Residential' });
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Add Building
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailsPage;

